import InfoModal from '@/components/molecules/InfoModal';
import ACCreationForm, { ACCreationFormHandle } from '@/components/organisms/ACCreationForm';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { accountingCenterService } from '@/services/accounting-centers.service';
import { budgetMemberService } from '@/services/budget-member.service';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import ACCreationSteps from '@/components/molecules/accountingCenter/ACCreationSteps';
import ACCreationStepsMobile from '@/components/molecules/accountingCenter/ACCreationStepsMobile';
import ACMemberCalculationsFooter from '@/components/molecules/accountingCenter/ACMemberCalculationsFooter';
import { Button } from '@/components/ui/button';
import { budgetService } from '@/services/budget.service';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { getCurrentUser } from '@/store/slices/auth';
import { AccountingCenterMemberFormValues, AccountingCenter } from '@/services/types/accounting-centers-types';
import { ACMemberCalculations } from '@/utils/accountingCenterCalculations';
import {
  mapBudgetMembersToACCreationMembers,
  getACFormMaxValues,
  getDefaultACPool,
  resolveDefaultAccountingCenter
} from '@/utils/accountingCenterMembers';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';

const formatACTitle = (acType?: string) =>
  acType
    ? acType
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : '';

const ACAddPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [maxExpenseValue, setMaxExpenseValue] = useState(0);
  const [maxRevenueValue, setMaxRevenueValue] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [memberCalculations, setMemberCalculations] = useState<ACMemberCalculations | null>(null);
  const [initialMembers, setInitialMembers] = useState<AccountingCenterMemberFormValues[]>([]);

  const navigate = useNavigate();
  const { budgetId, acType } = useParams();
  const location = useLocation();
  const defaultACFromState = location.state?.defaultAC as
    | { acmev?: number; acrt?: number; members?: any[] }
    | undefined;
  const currentUser = useSelector(getCurrentUser);

  const formRef = useRef<ACCreationFormHandle>(null);

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}/accounting-centers/${acType}`,
      // This page is always pushed from the AC list page above, so cancelling
      // should pop back to it rather than replace (which would create a
      // duplicate consecutive history entry and break the back button).
      backOnCancel: true
    });

  const fetchInitialData = async () => {
    if (!budgetId) {
      setLoadError('Budget ID not found');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError(null);

      const acTypeUpper = String(acType ?? '').toUpperCase();
      const [{ data: budget }, membersResponse, acsResponse] = await Promise.all([
        budgetService.getBudget(budgetId),
        budgetMemberService.getMembers(budgetId),
        accountingCenterService.getAccountingCentersByType(budgetId, acTypeUpper)
      ]);

      setCurrency(budget.currency ?? 'EUR');

      const defaultAC = resolveDefaultAccountingCenter(
        acsResponse.data ?? [],
        defaultACFromState as Partial<AccountingCenter> | undefined
      );

      const pool = getDefaultACPool(defaultAC, budget);
      const maxValues = getACFormMaxValues(pool);
      setMaxExpenseValue(maxValues.acmev);
      setMaxRevenueValue(maxValues.acrt);

      const members = mapBudgetMembersToACCreationMembers(
        membersResponse.data ?? [],
        defaultAC?.members ?? defaultACFromState?.members ?? []
      );
      setInitialMembers(members);
    } catch (err: any) {
      console.error('Error loading accounting center creation data:', err);
      setLoadError(err?.message ?? 'Failed to load accounting center details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [budgetId, acType]);

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      if (!budgetId) {
        throw new Error('Budget ID is required');
      }

      const acMembers = formRef.current?.getMembers() ?? [];
      const memberAllocations = acMembers
        .filter((member) => member.memberId && Number(member.oreHours) > 0)
        .map((member) => ({
          member_id: member.memberId!,
          ore_hours: Number(member.oreHours)
        }));

      await accountingCenterService.createAccountingCenter(budgetId, {
        ...data,
        type: acType?.toUpperCase(),
        ...(memberAllocations.length > 0 ? { member_allocations: memberAllocations } : {})
      });

      toast.success('Accounting center created successfully');
      allowNavigation();
      // This page was pushed from the AC list page, so pop back to it instead
      // of replacing — replacing to the same URL as the previous history
      // entry would create a duplicate entry and break the back button.
      navigate(-1);
    } catch (error: any) {
      console.error('Error creating accounting center:', error);
      toast.error(error?.message ?? 'Failed to create accounting center. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRightButtonClick = async () => {
    if (activeStep === 1) {
      const isValid = await formRef.current?.validateStep(1);
      if (isValid) {
        setActiveStep(2);
      }
      return;
    }

    const isValid = await formRef.current?.validateStep(2);
    if (isValid) {
      formRef.current?.submit();
    }
  };

  const handleLeftButtonClick = () => {
    if (activeStep === 2) {
      setActiveStep(1);
      return;
    }
    handleCancel();
  };

  const isLastStep = activeStep === 2;

  if (isLoading) {
    return (
      <div className="border flex-1 rounded-[16px] p-4 bg-white min-h-[320px] flex items-center justify-center gap-2">
        <Loader2 className="size-5 animate-spin text-primary" />
        <p className="text-secondary text-sm">Loading accounting center details...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="border flex-1 rounded-[16px] p-4 bg-white min-h-[320px] flex flex-col items-center justify-center gap-3">
        <p className="text-red-500 text-sm text-center">{loadError}</p>
        <Button variant="primary-reverse" className="rounded-full px-5" onClick={fetchInitialData}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <MobileAppBar title={<span className="capitalize">{acType?.split('_')[0]} Accounting Center Creation</span>} />

      <div className="flex-1 bg-[#FBFBFF] p-4 md:bg-white rounded-[16px]">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-3 md:mb-0">
          <h2 className="text-center font-bold mb-1 md:text-left md:text-[24px] md:mb-6">
            Create your {formatACTitle(acType)} Accounting Center
          </h2>

          <ACCreationSteps activeStep={activeStep} className="shrink-0 mb-6" />
        </div>

        <div className="md:hidden mb-6">
          <ACCreationStepsMobile activeStep={activeStep} />
        </div>

        <ACCreationForm
          ref={formRef}
          activeStep={activeStep}
          onSubmit={handleSubmit}
          maxExpenseValue={maxExpenseValue}
          maxRevenueValue={maxRevenueValue}
          currency={currency}
          isSubmitting={isSubmitting}
          initialMembers={initialMembers}
          currentUserEmail={currentUser?.email}
          currentUserPhotoUrl={currentUser?.photoURL}
          onMemberCalculationsChange={setMemberCalculations}
          onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
        />
      </div>

      <footer className="flex flex-col md:flex-row items-end justify-between gap-4 px-4 py-7 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent">
        <div className="flex-1 w-full md:w-auto min-w-0">
          {activeStep === 2 && <ACMemberCalculationsFooter calculations={memberCalculations} />}
        </div>
        <div className="w-full md:w-auto flex justify-end gap-4 shrink-0 mt-2 md:mt-0">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant="text"
            onClick={handleLeftButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {activeStep === 1 ? 'Cancel' : 'Previous'}
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            onClick={handleRightButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? 'Creating...' : isLastStep ? 'Create' : 'Next'}
          </Button>
        </div>
      </footer>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your accounting center"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}
    </>
  );
};

export default ACAddPage;
